// apps/dashboard/src/components/DunningBanner.tsx
// Persistent top-of-layout banner shown to tenants with PAST_DUE or SUSPENDED subscriptions.
// Position: above the main content area, below TrialBanner.
// SPEC: docs/specs/billing/sprint-an-dunning.spec.md

import { useDunningStatus } from '@/hooks/use-dunning';
import { cn } from '@/lib/cn';
import { AlertTriangle, XCircle } from 'lucide-react';

export function DunningBanner() {
  const { data } = useDunningStatus();

  if (!data) return null;
  if (data.status !== 'PAST_DUE' && data.status !== 'SUSPENDED') return null;

  const isSuspended = data.status === 'SUSPENDED';
  const isCritical =
    isSuspended || (data.daysUntilSuspension !== null && data.daysUntilSuspension <= 1);

  let message: string;
  let ctaLabel: string;

  if (isSuspended) {
    message = 'Conta suspensa. Regularize o pagamento para reativar o acesso.';
    ctaLabel = 'Reativar conta';
  } else if (data.daysUntilSuspension !== null && data.daysUntilSuspension <= 1) {
    const when = data.daysUntilSuspension === 0 ? 'hoje' : 'amanhã';
    message = `Sua conta será suspensa ${when} por falta de pagamento.`;
    ctaLabel = 'Regularizar agora';
  } else {
    const days = data.daysUntilSuspension;
    message = `Pagamento pendente — atualize seu cartão para evitar a suspensão${days !== null ? ` em ${days} dia${days !== 1 ? 's' : ''}` : ''}.`;
    ctaLabel = 'Atualizar pagamento';
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 px-4 py-2 text-sm font-medium',
        isCritical
          ? 'bg-destructive/10 text-destructive border-b border-destructive/20'
          : 'bg-yellow-50 text-yellow-800 border-b border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-700/40',
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 min-w-0">
        {isCritical ? (
          <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <span className="truncate">{message}</span>
      </div>

      <a
        href="/settings/billing"
        className={cn(
          'shrink-0 rounded-md px-3 py-1 text-xs font-semibold transition-colors',
          isCritical
            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            : 'bg-yellow-700 text-white hover:bg-yellow-800 dark:bg-yellow-500 dark:text-yellow-950 dark:hover:bg-yellow-400',
        )}
      >
        {ctaLabel}
      </a>
    </div>
  );
}
